/**
 * Storing, reading back and undoing a business-outcome upload.
 *
 * Writes go through `apply_outcome_import`, which runs as the caller: RLS
 * already answers whether this person may write this workspace, and a
 * definer-rights function would replace that answer with the argument the
 * caller passed in.
 *
 * @module services/outcomeImport/outcomeImportService
 */

import { getSupabaseClient } from '@/config';
import { nullableArg } from "../dbBoundary";
import { logError } from '@/services/logger';
import type { TFn } from '@/services/brokerImport/parseBrokerCsv';
import type { ParsedOutcomeRow } from './parseOutcomeCsv';

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface OutcomeImportRecord {
  id: string;
  filename: string;
  periodStart: string;
  periodEnd: string;
  rowCount: number;
  status: string;
  uploadedAt: string;
}

/** What is already stored: for the days a file is about to overwrite, or in total. */
export interface ExistingCoverage {
  days: number;
  firstDate: string | null;
  lastDate: string | null;
}

/**
 * How much is already there — for a date range, or for the whole workspace when
 * `period` is null.
 *
 * Shown before applying, because an upload REPLACES the days it covers. Somebody
 * re-sending a corrected March should be told that 31 days are about to be
 * rewritten; somebody sending March for the first time should not be alarmed.
 *
 * Counted in SQL rather than by reading the rows back. This used to select every
 * matching row and count the distinct dates client-side, which PostgREST caps at
 * 1,000 rows with HTTP 200 and no error: a client whose outcomes are split five
 * ways crosses that at seven months, and the truncation UNDERSTATES what the
 * upload is about to replace — on the one screen whose job is to warn about it.
 */
export const fetchExistingCoverage = async (
  workspaceId: string,
  period: { start: string; end: string } | null,
  t: TFn,
): Promise<ServiceResult<ExistingCoverage>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };

  // A NULL bound means unbounded. Postgres parameters are always nullable and
  // the generated Args type cannot say so. The note stays OUT here: a comment
  // inside the argument literal hides the key after it from the
  // rpcArgumentNames guard.
  const { data, error } = await supabase.rpc('get_outcome_coverage', {
    p_workspace_id: workspaceId,
    p_start: nullableArg(period?.start ?? null),
    p_end: nullableArg(period?.end ?? null),
  });

  if (error) {
    logError('OutcomeImport', 'Failed reading existing coverage', error);
    return { success: false, error: t('outcomeImport.readExistingFailed') };
  }

  // A grouping function with nothing to group returns one row of NULLs, not
  // zero rows — but guard both, since an empty array is what a filtered-out
  // workspace looks like.
  const row = (data as Array<{ days: number | null; first_date: string | null; last_date: string | null }> | null)?.[0];
  return {
    success: true,
    data: {
      days: Number(row?.days ?? 0),
      firstDate: row?.first_date ?? null,
      lastDate: row?.last_date ?? null,
    },
  };
};

/**
 * One line of what the client reported, for the channel they credited it to.
 *
 * `platform` is null for whole-day rows — a different KIND of row, not a missing
 * value. Every figure is nullable for the same reason: a client who sends leads
 * and no revenue has no revenue, which is not revenue of zero.
 */
export interface OutcomePlatformLine {
  platform: string | null;
  days: number;
  leads: number | null;
  bookings: number | null;
  sales: number | null;
  revenue: number | null;
}

/**
 * What was uploaded, grouped by the channel the client credited.
 *
 * This is "as reported" and nothing more. Their split is almost always
 * last-click or a how-did-you-hear-about-us field, both of which credit
 * whichever channel sat nearest the purchase — the blindness marketing-mix
 * modelling exists to correct. The model reads the daily TOTAL and never the
 * split, so this is shown for the client to check their own upload against,
 * never as this product's attribution.
 */
export const fetchOutcomeBreakdown = async (
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult<OutcomePlatformLine[]>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };

  const { data, error } = await supabase.rpc('get_outcome_breakdown', {
    p_workspace_id: workspaceId,
  });

  if (error) {
    logError('OutcomeImport', 'Failed reading outcome breakdown', error);
    return { success: false, error: t('outcomeImport.readBreakdownFailed') };
  }

  const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return {
    success: true,
    data: rows.map((r) => ({
      platform: (r.platform as string | null) ?? null,
      days: Number(r.days ?? 0),
      leads: num(r.leads),
      bookings: num(r.bookings),
      sales: num(r.sales),
      revenue: num(r.revenue),
    })),
  };
};

/** Spend of the outcome days in one account currency (migration 125). currency null = an account of unknown currency. */
export interface OutcomeSpend {
  currency: string | null;
  /** null = every cost in this currency was NULL. */
  spend: number | null;
}

/** Imported outcomes and ad spend over one period, from one provenance. */
export interface OutcomePeriod {
  /** Any active whole-day outcome row ever, in this workspace. false = the box is hidden. */
  anyImported: boolean;
  /** Days in the period with an active whole-day row. */
  days: number;
  leads: number | null;
  bookings: number | null;
  sales: number | null;
  revenue: number | null;
  /**
   * Spend from media_ad_set_metrics (no analytics) over the SAME days `days`
   * counts, never the whole period. null = no metric rows on those days ("not
   * synced"), which is not "spent nothing".
   */
  spend: number | null;
  /**
   * `spend` split by account currency (one bucket per code after the trim),
   * most spent first. [] = no metric rows on those days (spend null), or a
   * database without migration 125. Never summed across currencies (FR-X-02).
   */
  spendByCurrency: OutcomeSpend[];
}

/**
 * Codes equal after the trim ('USD' and 'USD ', '' and NULL) are one bucket: their spend is summed, a NULL spend
 * adding nothing (NULL + NULL stays NULL). Then the server's order again: spend descending, NULL last, then the
 * currency, NULL last.
 */
const mergedSpendsOf = (spends: readonly OutcomeSpend[]): OutcomeSpend[] => {
  // Built locally and never shared.
  const byCurrency = new Map<string | null, number | null>();
  spends.forEach(({ currency, spend }) => {
    const before = byCurrency.get(currency) ?? null;
    byCurrency.set(currency, spend === null ? before : (before ?? 0) + spend);
  });
  const nullLast = (a: unknown, b: unknown): number => Number(a === null) - Number(b === null);
  return [...byCurrency.entries()]
    .map(([currency, spend]) => ({ currency, spend }))
    .sort((a, b) => nullLast(a.spend, b.spend) || (b.spend ?? 0) - (a.spend ?? 0)
      || nullLast(a.currency, b.currency) || (a.currency ?? '').localeCompare(b.currency ?? ''));
};

/**
 * Whole-day outcomes and Supabase-side ad spend for a period (null = all
 * time). NULL sums stay null: "not imported" is not zero. Runs as the caller,
 * so RLS decides what is summed.
 */
export const fetchOutcomePeriod = async (
  workspaceId: string,
  period: { start: string; end: string } | null,
  t: TFn,
): Promise<ServiceResult<OutcomePeriod>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };

  // The function is newer than the generated types (a format-only drift the
  // project keeps), so its call is typed here rather than regenerated.
  const rpc = supabase.rpc.bind(supabase) as unknown as (
    fn: 'get_outcome_period',
    args: { p_workspace_id: string; p_start: string | null; p_end: string | null },
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  const { data, error } = await rpc('get_outcome_period', {
    p_workspace_id: workspaceId,
    p_start: period?.start ?? null,
    p_end: period?.end ?? null,
  });

  const row = ((data ?? []) as Array<Record<string, unknown>>)[0];
  if (error || !row) {
    logError('OutcomeImport', 'Failed reading outcome period', error);
    return { success: false, error: t('outcomeImport.readBreakdownFailed') };
  }

  const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
  // jsonb arrives parsed. Anything but an array (NULL: no metric rows) is no bucket.
  const buckets = (Array.isArray(row.spend_by_currency) ? row.spend_by_currency : []) as Array<Record<string, unknown> | null>;
  return {
    success: true,
    data: {
      anyImported: Boolean(row.any_imported),
      days: Number(row.days ?? 0),
      leads: num(row.leads),
      bookings: num(row.bookings),
      sales: num(row.sales),
      revenue: num(row.revenue),
      spend: num(row.spend),
      spendByCurrency: mergedSpendsOf(buckets.map((b) => ({
        currency: b?.currency ? String(b.currency).trim() || null : null,
        spend: num(b?.spend),
      }))),
    },
  };
};

export const applyOutcomeImport = async (
  workspaceId: string,
  filename: string,
  rows: ParsedOutcomeRow[],
  t: TFn,
): Promise<ServiceResult<{ batchId: string }>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };
  if (rows.length === 0) return { success: false, error: t('outcomeImport.noRows') };

  // Empty strings rather than nulls: the function reads every field with
  // NULLIF(…, '')::type, so one shape covers "absent" for all of them.
  const payload = rows.map((r) => ({
    date: r.date,
    platform: r.platform ?? '',
    leads: r.leads ?? '',
    bookings: r.bookings ?? '',
    sales: r.sales ?? '',
    revenue: r.revenue ?? '',
  }));

  try {
    const { data, error } = await supabase.rpc('apply_outcome_import', {
      p_workspace: workspaceId,
      p_filename: filename,
      p_rows: payload,
    });

    if (error) {
      logError('OutcomeImport', 'Import failed', error);
      // 23505 is the one-active-row-per-day index. The parser catches the same
      // shape first and names a line, so reaching here means something the file
      // could not show — say what it means rather than showing the raw code.
      const message = error.code === '23505'
        ? t('outcomeImport.duplicateDay')
        : t('outcomeImport.importFailed');
      return { success: false, error: message };
    }
    return { success: true, data: { batchId: data as string } };
  } catch (err) {
    logError('OutcomeImport', 'Import failed', err);
    return { success: false, error: t('outcomeImport.importFailed') };
  }
};

export const revertOutcomeImport = async (
  batchId: string,
  t: TFn,
): Promise<ServiceResult<{ archived: number }>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };

  try {
    const { data, error } = await supabase.rpc('revert_outcome_import', { p_batch: batchId });
    if (error) {
      logError('OutcomeImport', 'Revert failed', error);
      return { success: false, error: t('outcomeImport.revertFailed') };
    }
    return { success: true, data: { archived: Number(data ?? 0) } };
  } catch (err) {
    logError('OutcomeImport', 'Revert failed', err);
    return { success: false, error: t('outcomeImport.revertFailed') };
  }
};

export const fetchOutcomeImports = async (
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult<OutcomeImportRecord[]>> => {
  const supabase = getSupabaseClient();
  if (!supabase) return { success: false, error: t('invite.errNotConfigured') };

  const { data, error } = await supabase
    .from('outcome_imports')
    .select('id, filename, period_start, period_end, row_count, status, uploaded_at')
    .eq('workspace_id', workspaceId)
    .order('uploaded_at', { ascending: false })
    .limit(20);

  if (error) {
    logError('OutcomeImport', 'Failed reading import history', error);
    return { success: false, error: t('outcomeImport.historyFailed') };
  }

  return {
    success: true,
    data: (data ?? []).map((r) => ({
      id: String(r.id),
      filename: String(r.filename),
      periodStart: String(r.period_start),
      periodEnd: String(r.period_end),
      rowCount: Number(r.row_count),
      status: String(r.status),
      uploadedAt: String(r.uploaded_at),
    })),
  };
};
